import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

import Coupons from '../../Components/admin/CouponManager';
import Login from '../../Components/admin/Login';
import Sidebar from '../../Components/admin/Sidebar';
import MarketplaceOrders from '../../Components/admin/MarketplaceOrders';
import TherapistVerificationQueue from '../../Components/admin/TherapistVerificationQueue';
import VenueVerificationQueue from '../../Components/admin/VenueVerificationQueue';
import DisputeQueue from '../../Components/admin/DisputeQueue';
import ZoneManager from '../../Components/admin/ZoneManager';
import ServiceManager from '../../Components/admin/ServiceManager';
import UserManagement from '../../Components/admin/UserManagement';
import WithdrawalQueue from '../../Components/admin/WithdrawalQueue';
import DashboardOverview from '../../Components/admin/DashboardOverview';
import FlaggedReports from '../../Components/admin/FlaggedReports';

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('isAuthenticated') === 'true'
  );
  const [activeTab, setActiveTab] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);

  // The isAuthenticated flag in localStorage can go stale (e.g. the session
  // cookie expired, or someone else logged out this browser). Once the real
  // session has loaded, fall back to the login screen if it doesn't actually
  // belong to an admin, instead of trusting the flag indefinitely.
  useEffect(() => {
    if (!authLoading && isAuthenticated && (!user || user.role !== 'admin')) {
      localStorage.removeItem('isAuthenticated');
      setIsAuthenticated(false);
    }
  }, [authLoading, user, isAuthenticated]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsOpen(!mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update activeTab based on current path
  useEffect(() => {
    const path = window.location.pathname;
    const currentTab = path.split('/').pop();
    setActiveTab(currentTab);
  }, []);

  if (isAuthenticated && authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Login setIsAuthenticated={setIsAuthenticated} />;
  }

  return (
    <div className="flex h-screen bg-primary-lighter overflow-hidden">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          if (isMobile) setIsOpen(false);
        }}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isMobile={isMobile}
      />

      {/* Mobile overlay while the sidebar is open */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)} />
      )}

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        isMobile ? 'ml-0' : isOpen ? 'md:ml-64' : 'md:ml-16'
      }`}>
        {isMobile && (
          <div className="bg-white border-b shadow-sm px-4 py-3 flex items-center gap-3 shrink-0">
            <button onClick={() => setIsOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100">
              <Menu size={22} />
            </button>
            <h1 className="font-semibold text-dim">Admin Dashboard</h1>
          </div>
        )}

        <main className="flex-1 overflow-y-auto bg-primary-lighter p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="dashboard" element={<DashboardOverview />} />
              <Route path="coupons" element={<Coupons />} />
              <Route path="marketplace-orders" element={<MarketplaceOrders />} />
              <Route path="therapist-verification" element={<TherapistVerificationQueue />} />
              <Route path="venue-verification" element={<VenueVerificationQueue />} />
              <Route path="disputes" element={<DisputeQueue />} />
              <Route path="flagged-reports" element={<FlaggedReports />} />
              <Route path="zones" element={<ZoneManager />} />
              <Route path="services" element={<ServiceManager />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="withdrawals" element={<WithdrawalQueue />} />
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
